// PreviewResultPage — port of Backup/templates/quralyst_research/preview_result.html.
// Loads the result detail (resultsService.get) and renders the breadcrumb + header + a DataTable
// built from the result's columns/rows. Expandable cells open the shared CellModal.
import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DataTable, Spinner } from '@/components/ui';
import type { Column } from '@/components/ui';
import QueryError from '@/components/feedback/QueryError';
import { resultsService } from '@/services/api';
import type { ResultDetail } from '@/types';
import { useResultSummaryMeta } from '@/features/results/useResultSummaryMeta';
import { resultHeadingName } from '@/components/layout/breadcrumbs';
import { useCrumbEntity } from '@/hooks/useCrumbEntity';
import { useToast } from '@/hooks/useToast';
import { formatDateTimeLong, parseBackendDate } from '@/lib/datetime';
import { smoothedExportPercent, type FileDownloadProgress } from '@/lib/saveDownload';
import CommentModal from '@/features/results/viewResult/CommentModal';
import { commentsColumn } from '@/features/results/viewResult/commentsColumn';
import { useCommentCounts } from '@/features/results/viewResult/useCommentCounts';
import '@/styles/pages/preview-result.css';

type RowData = Record<string, string>;

function isoWeek(date: Date): number {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function uploadedOn(iso: string): string {
  const date = parseBackendDate(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `Uploaded on Week ${isoWeek(date)} on ${formatDateTimeLong(iso)}`;
}

export default function PreviewResultPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId');
  const toast = useToast();
  const { data: summary } = useResultSummaryMeta(fileId ? resultId : undefined);
  useCrumbEntity(fileId ? 'Preview Input File' : undefined);
  const [result, setResult] = useState<ResultDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState<FileDownloadProgress | null>(null);
  const [downloadElapsedMs, setDownloadElapsedMs] = useState(0);
  const downloadDone = downloadProgress?.phase === 'complete';
  const downloadBusy = downloadProgress != null && downloadProgress.phase !== 'complete';
  const downloadPercent = downloadDone
    ? 100
    : smoothedExportPercent(downloadElapsedMs, downloadProgress?.percent ?? 0);

  useEffect(() => {
    if (!downloadBusy) return;
    const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const tick = () => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      setDownloadElapsedMs(now - started);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [downloadBusy]);
  // Read-only comments (Phase 29): viewable on the preview, editor controls hidden
  // (legacy "comment viewing only" behavior).
  const [commentTarget, setCommentTarget] = useState<string | null>(null);
  const commentCounts = useCommentCounts(resultId);

  useEffect(() => {
    if (!resultId) return;
    let active = true;
    setLoading(true);
    setError(false);
    const load = fileId
      ? resultsService.getInputFilePreview(resultId, fileId)
      : resultsService.get(resultId);
    load
      .then((data) => {
        if (active) setResult(data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resultId, fileId, reloadKey]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <QueryError onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  if (!result) {
    return (
      <div className="preview-no-data text-center text-muted py-5">
        <i className="bi bi-table" />
        <h5 className="mt-3">No Data Available</h5>
        <p>The data could not be loaded or is empty.</p>
      </div>
    );
  }

  const isInputFilePreview = Boolean(result.isInputFilePreview || fileId);
  const resultsCount = result.rows.length;
  const columnsCount = result.columns.length;
  const matchCount = summary?.totalMatches;
  const fileLabel = result.inputFileName || 'Input file';
  const runTitle = resultHeadingName(undefined, summary?.resultFilename, summary?.filtersApplied);

  const columns: Column<RowData>[] = result.columns.map((col) => ({
    key: col,
    header: col,
    expandable: true,
    width: isInputFilePreview ? 160 : undefined,
  }));

  // Comments only apply to result rows (not input-file previews) and need a company column.
  const nameCol = result.columns.find((c) => c.trim().toLowerCase().includes('company name'));
  if (!isInputFilePreview && nameCol) {
    columns.unshift(commentsColumn(nameCol, commentCounts, setCommentTarget));
  }

  if (isInputFilePreview) {
    return (
      <div className="input-file-preview">
        <div className="input-file-preview__head">
          <div>
            <h1 className="input-file-preview__title">{runTitle}</h1>
            {result.createdAt ? (
              <p className="input-file-preview__meta">{uploadedOn(result.createdAt)}</p>
            ) : null}
          </div>
          {resultId && fileId ? (
            <button
              type="button"
              className={`btn btn-standard rd-export-btn${downloadDone ? ' rd-dl-done' : ''}${downloadBusy ? ' is-busy' : ''}`}
              onClick={() => {
                if (downloadBusy || downloadDone) return;
                setDownloadElapsedMs(0);
                setDownloadProgress({ phase: 'starting', percent: 0, etaSeconds: null });
                void resultsService
                  .downloadInputFile(resultId, fileId, setDownloadProgress)
                  .catch((err: { message?: string }) => {
                    setDownloadProgress(null);
                    toast.error(err?.message ?? 'Download failed.');
                  });
              }}
              disabled={downloadBusy || downloadDone}
              aria-busy={downloadBusy}
              aria-live="polite"
            >
              {downloadBusy ? (
                <span
                  className="rd-export-btn__fill"
                  style={{ width: `${downloadPercent}%` }}
                  aria-hidden="true"
                />
              ) : null}
              <span className="rd-export-btn__content">
                {downloadDone ? (
                  <Check style={{ width: 14, height: 14 }} aria-hidden />
                ) : downloadBusy ? (
                  <Loader2
                    className="rd-export-btn__spin"
                    style={{ width: 14, height: 14 }}
                    aria-hidden
                  />
                ) : (
                  <i className="bi bi-download" aria-hidden="true" />
                )}
                {downloadDone
                  ? 'Downloaded'
                  : downloadBusy
                    ? `Downloading ${downloadPercent}%`
                    : 'Download CSV'}
              </span>
            </button>
          ) : null}
        </div>
        <section className="input-file-preview__sheet">
          <h2 className="input-file-preview__file">Preview: Input File - {fileLabel}</h2>
          <div className="input-file-preview__counts">
            <span className="input-file-preview__count">{columnsCount} columns</span>
            {typeof matchCount === 'number' ? (
              <span className="input-file-preview__count input-file-preview__count--matches">
                {matchCount.toLocaleString()} Matches
              </span>
            ) : null}
            <span className="input-file-preview__count input-file-preview__count--results">
              {resultsCount.toLocaleString()} results
            </span>
          </div>
          <DataTable<RowData>
            columns={columns}
            rows={result.rows}
            emptyMessage="The data could not be loaded or is empty."
          />
        </section>
      </div>
    );
  }

  return (
    <>
      {/* Data Table Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <h5 className="table-title mb-0">
          <span className="badge ms-2">{resultsCount} rows</span>
          <span className="badge ms-2">{columnsCount} columns</span>
        </h5>
      </div>

      {/* Data Table — plain shell; DataTable's own .table-responsive is the single scroll container
          (wrapping it in another .table-responsive/.table-container gave two nested scrollbars). */}
      <div id="results-table">
        <DataTable<RowData>
          columns={columns}
          rows={result.rows}
          emptyMessage="The data could not be loaded or is empty."
        />
      </div>

      {resultId && (
        <CommentModal
          resultId={resultId}
          companyName={commentTarget}
          onClose={() => setCommentTarget(null)}
          readOnly
        />
      )}
    </>
  );
}
